import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { BasketProvider } from './contexts/BasketContext';
import { PageReadyProvider, usePageReady } from './contexts/PageReadyContext';
import { Layout } from './components/Layout/Layout';
import { UpdateNotification } from './components/UpdateNotification/UpdateNotification';
import { InstallPrompt } from './components/InstallPrompt/InstallPrompt';
import { PreviewBanner } from './components/PreviewBanner/PreviewBanner';
import { HomePage } from './pages/HomePage';
import { ContentPage } from './pages/ContentPage';
import { initGtm } from '@shared/seo/gtm';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { config } from './config';
import './i18n';
import './App.css';

const ReservationPage = lazy(() => import('./pages/ReservationPage').then(m => ({ default: m.ReservationPage })));
const GroupClassesPage = lazy(() => import('./pages/GroupClassesPage').then(m => ({ default: m.GroupClassesPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const BasketPage = lazy(() => import('./pages/BasketPage').then(m => ({ default: m.BasketPage })));
const ProfilePageAdapter = lazy(() => import('./pages/ProfilePageAdapter').then(m => ({ default: m.ProfilePageAdapter })));
const LeaveReviewPage = lazy(() => import('./pages/LeaveReviewPage').then(m => ({ default: m.LeaveReviewPage })));
const WaitlistOfferPage = lazy(() => import('./pages/WaitlistOfferPage').then(m => ({ default: m.WaitlistOfferPage })));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelledPage = lazy(() => import('./pages/PaymentCancelledPage').then(m => ({ default: m.PaymentCancelledPage })));
const QuestionnairePage = lazy(() => import('./pages/QuestionnairePage').then(m => ({ default: m.QuestionnairePage })));

/**
 * Loads Google Tag Manager only after the page has fully rendered (pageReady=true).
 * GTM is a heavy script that blocks the main thread for 200–300ms on mobile; if
 * it runs during the LCP paint window it pushes the largest-contentful-paint
 * time out by that amount. Gating on pageReady ensures it runs after paint.
 */
const GtmInitializer: React.FC = () => {
  const { pageReady } = usePageReady();
  useEffect(() => {
    if (!pageReady || !config.gtmId) return;
    const load = () => initGtm(config.gtmId);
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(load, { timeout: 2000 });
    } else {
      setTimeout(load, 500);
    }
  }, [pageReady]);
  return null;
};

function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!config.pwaEnabled) return;

    // Set dynamic manifest link for per-tenant PWA branding.
    // The manifest is served by the API (cross-origin), so pass our origin -
    // the endpoint uses it to emit absolute start_url/scope, which must be
    // same-origin with the document or browsers ignore them.
    if (config.apiUrl && config.sitePath) {
      const link = document.getElementById('manifest-link');
      if (link) {
        const origin = encodeURIComponent(window.location.origin);
        link.setAttribute('href', `${config.apiUrl.replace(/\/$/, '')}/api/sites/manifest/${config.sitePath}?origin=${origin}`);
      }
    }

    serviceWorkerRegistration.register({
      onUpdate: () => setUpdateAvailable(true),
    });
  }, []);

  return (
    <SiteSettingsProvider>
      <CustomerAuthProvider>
        <BasketProvider>
        <PageReadyProvider>
        <GtmInitializer />
        <PreviewBanner />
        <Router>
          <Suspense fallback={<div aria-busy="true" aria-live="polite" style={{ minHeight: '50vh' }} />}>
            <Routes>
              <Route path="/review/:token" element={<LeaveReviewPage />} />
              {/* The claim link in the offer SMS/e-mail. Outside Layout, like /review. */}
              <Route path="/waitlist-offer/:token" element={<WaitlistOfferPage />} />
              <Route path="/*" element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/rezerwacje" element={<ReservationPage />} />
                    <Route path="/zajecia-grupowe" element={<GroupClassesPage />} />
                    <Route path="/logowanie" element={<LoginPage />} />
                    <Route path="/koszyk" element={<BasketPage />} />
                    <Route path="/moje-konto" element={<ProfilePageAdapter />} />
                    <Route path="/payment-success" element={<PaymentSuccessPage />} />
                    <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
                    <Route path="/questionnaire/:submissionId" element={<QuestionnairePage />} />
                    <Route path="/*" element={<ContentPage />} />
                  </Routes>
                </Layout>
              } />
            </Routes>
          </Suspense>
        </Router>
        <UpdateNotification visible={updateAvailable} />
        <InstallPrompt />
        </PageReadyProvider>
        </BasketProvider>
      </CustomerAuthProvider>
    </SiteSettingsProvider>
  );
}

export default App;
